import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Alert,
  CircularProgress,
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link, useNavigate } from "react-router-dom";
import { useGetAllStaffQuery, useDeleteUserMutation } from '../../services/userCRUDApi'; // Adjust path if needed
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
// import './member.css'; // Create this file if you want custom CSS, similar to staff.css

function Member() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isSmallScreen ? 5 : 10);
  const [success, setSuccess] = useState(false);
  const { data: allStaff, isLoading } = useGetAllStaffQuery();
  const [deleteUser] = useDeleteUserMutation();
  const navigate = useNavigate();

  // Filter data to only show staff with type/role 'STAFF'
  const filteredStaff = allStaff ? allStaff.filter(staff => staff.type === 'STAFF') : []; // Change 'type' to 'role' if your API uses 'role'

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id) => {
    navigate(`/admin/member/edit/${id}`); // Kept your original edit path
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: 'Confirm to Delete Staff Member',
      message: 'Are you sure you want to delete this staff member?',
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await deleteUser(id).unwrap();
              setSuccess(true);
              setTimeout(() => setSuccess(false), 3000);
            } catch (error) {
              console.error("Error deleting user:", error);
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const paginatedStaff = filteredStaff.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box className="staff-container"> {/* Reuse class from example, or rename to member-container */}
      {/* Header */}
      <Box className="staff-header">
        <Typography variant="h5" className="staff-title">
          Staff Members
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {success && (
            <Alert
              severity="success"
              onClose={() => setSuccess(false)}
              sx={{ mb: isSmallScreen ? 1 : 0 }}
            >
              Staff member deleted
            </Alert>
          )}
          <Button
            component={Link}
            to="/admin/newstaff"
            variant="contained"
            color="primary"
            size={isSmallScreen ? 'small' : 'medium'}
            className="add-staff-button"
          >
            {isSmallScreen ? 'Add' : 'Add New Staff'}
          </Button>
        </Box>
      </Box>

      {isSmallScreen ? (
        /* Mobile Card View */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {paginatedStaff.length > 0 ? (
            paginatedStaff.map((staff) => (
              <Card key={staff.id} className="staff-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {staff.first_Name} {staff.last_Name} {/* Adjust keys if needed */}
                    </Typography>
                    <Chip
                      label={staff.type}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <Box component="span" fontWeight="bold">Email:</Box> {staff.email}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <Box component="span" fontWeight="bold">Phone:</Box> {staff.phone_Number} {/* Adjust if 'phone' */}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={() => handleEdit(staff.id)}
                      fullWidth
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={() => handleDelete(staff.id)}
                      color="error"
                      fullWidth
                    >
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography variant="body1" textAlign="center" py={4}>
              No staff members found
            </Typography>
          )}
        </Box>
      ) : (
        /* Desktop Table View */
        <Box sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#1a237e',
                borderRadius: '3px'
              }
            }}
          >
            <Table sx={{ minWidth: 650 }}>
              <TableHead sx={{ backgroundColor: '#004aad' }}>
                <TableRow>

                  <TableCell sx={{ color: '#fff', minWidth: '150px',backgroundColor: '#004aad' }}>Name</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '100px',backgroundColor: '#004aad'  }}>Type</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '180px' ,backgroundColor: '#004aad' }}>Email</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '120px' ,backgroundColor: '#004aad' }}>Phone</TableCell>
                  <TableCell sx={{ color: '#fff', minWidth: '100px',backgroundColor: '#004aad'  }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedStaff.length > 0 ? (
                  paginatedStaff.map((staff) => (
                    <TableRow key={staff.id} hover>
                      <TableCell>{staff.first_Name} {staff.last_Name}</TableCell>
                      <TableCell>{staff.type}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>{staff.email}</TableCell>
                      <TableCell>{staff.phone_Number}</TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(staff.id)}
                          aria-label="edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(staff.id)}
                          aria-label="delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No staff members found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0 }}>
                    <TablePagination
                      component="div"
                      count={filteredStaff.length}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[5, 10, 25]}
                      sx={{
                        backgroundColor: '#004aad',
                        color: '#fff',
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          color: '#fff',
                          fontSize: '0.875rem',
                          my: 0
                        },
                        '& .MuiSelect-icon': {
                          color: '#fff',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#fff',
                        },
                        '& .MuiInputBase-root': {
                          color: '#fff',
                        },
                        '& .MuiTablePagination-actions': {
                          marginLeft: 0
                        }
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Box>
      )}
      {/* Mobile Pagination */}
      {isSmallScreen && paginatedStaff.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredStaff.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ width: '100%' }}
          />
        </Box>
      )}
    </Box>
  );
}

export default Member;